import { getAnalytics, logEvent, setUserProperties } from 'firebase/analytics';
import { getPerformance } from 'firebase/performance';

let analytics = null;
let performance = null;

// Initialize analytics (call this after Firebase app is initialized)
export const initializeAnalytics = (app) => {
  try {
    analytics = getAnalytics(app);
    performance = getPerformance(app);
    console.log('✓ Analytics and Performance Monitoring initialized');
  } catch (error) {
    console.error('Analytics initialization failed:', error);
  }
};

// User tracking
export const setUser = (userId, properties = {}) => {
  if (!analytics) return;
  
  try {
    setUserProperties(analytics, {
      user_id: userId,
      ...properties
    });
  } catch (error) {
    console.error('Failed to set user properties:', error);
  }
};

// Band events
export const trackBandCreated = (bandId, bandName) => {
  if (!analytics) return;
  
  logEvent(analytics, 'band_created', {
    band_id: bandId,
    band_name: bandName,
    timestamp: new Date().toISOString()
  });
};

export const trackBandJoined = (bandId, inviteCode) => {
  if (!analytics) return;
  
  logEvent(analytics, 'band_joined', {
    band_id: bandId,
    invite_code: inviteCode,
    timestamp: new Date().toISOString()
  });
};

export const trackMemberInvited = (bandId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'member_invited', {
    band_id: bandId,
    timestamp: new Date().toISOString()
  });
};

export const trackMemberRemoved = (bandId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'member_removed', {
    band_id: bandId,
    timestamp: new Date().toISOString()
  });
};

// Song events
export const trackSongCreated = (bandId, songId, songName) => {
  if (!analytics) return;
  
  logEvent(analytics, 'song_created', {
    band_id: bandId,
    song_id: songId,
    song_name: songName,
    timestamp: new Date().toISOString()
  });
};

export const trackSongDeleted = (bandId, songId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'song_deleted', {
    band_id: bandId,
    song_id: songId,
    timestamp: new Date().toISOString()
  });
};

export const trackVersionUploaded = (bandId, songId, fileSize, fileType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'version_uploaded', {
    band_id: bandId,
    song_id: songId,
    file_size_kb: fileSize,
    file_type: fileType,
    timestamp: new Date().toISOString()
  });
};

export const trackVersionDeleted = (bandId, songId, versionId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'version_deleted', {
    band_id: bandId,
    song_id: songId,
    version_id: versionId,
    timestamp: new Date().toISOString()
  });
};

export const trackCommentAdded = (bandId, songId, versionId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'comment_added', {
    band_id: bandId,
    song_id: songId,
    version_id: versionId,
    timestamp: new Date().toISOString()
  });
};

export const trackLyricsUploaded = (bandId, songId, fileType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'lyrics_uploaded', {
    band_id: bandId,
    song_id: songId,
    file_type: fileType,
    timestamp: new Date().toISOString()
  });
};

// Gig events
export const trackGigCreated = (bandId, gigId, venue) => {
  if (!analytics) return;
  
  logEvent(analytics, 'gig_created', {
    band_id: bandId,
    gig_id: gigId,
    venue: venue,
    timestamp: new Date().toISOString()
  });
};

export const trackGigDeleted = (bandId, gigId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'gig_deleted', {
    band_id: bandId,
    gig_id: gigId,
    timestamp: new Date().toISOString()
  });
};

export const trackSetlistUploaded = (bandId, gigId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'setlist_uploaded', {
    band_id: bandId,
    gig_id: gigId,
    timestamp: new Date().toISOString()
  });
};

export const trackGigPhotoUploaded = (bandId, gigId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'gig_photo_uploaded', {
    band_id: bandId,
    gig_id: gigId,
    timestamp: new Date().toISOString()
  });
};

// Document events
export const trackDocumentUploaded = (bandId, documentType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'document_uploaded', {
    band_id: bandId,
    document_type: documentType,
    timestamp: new Date().toISOString()
  });
};

export const trackDocumentDeleted = (bandId, documentType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'document_deleted', {
    band_id: bandId,
    document_type: documentType,
    timestamp: new Date().toISOString()
  });
};

// User engagement
export const trackTabChanged = (tabName) => {
  if (!analytics) return;
  
  logEvent(analytics, 'tab_changed', {
    tab_name: tabName,
    timestamp: new Date().toISOString()
  });
};

export const trackSearch = (searchTerm) => {
  if (!analytics) return;
  
  logEvent(analytics, 'search', {
    search_term: searchTerm
  });
};

// Authentication events
export const trackLogin = (method = 'email') => {
  if (!analytics) return;
  
  logEvent(analytics, 'login', {
    method: method
  });
};

export const trackSignup = (method = 'email') => {
  if (!analytics) return;
  
  logEvent(analytics, 'sign_up', {
    method: method
  });
};

export const trackLogout = () => {
  if (!analytics) return;
  
  logEvent(analytics, 'logout', {
    timestamp: new Date().toISOString()
  });
};

// Settings events
export const trackSettingsChanged = (settingType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'settings_changed', {
    setting_type: settingType,
    timestamp: new Date().toISOString()
  });
};

export const trackInviteCodeCopied = (bandId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'invite_code_copied', {
    band_id: bandId,
    timestamp: new Date().toISOString()
  });
};

export const trackInviteCodeShared = (bandId) => {
  if (!analytics) return;
  
  logEvent(analytics, 'invite_code_shared', {
    band_id: bandId,
    timestamp: new Date().toISOString()
  });
};

// Feedback events
export const trackFeedbackSubmitted = (feedbackType) => {
  if (!analytics) return;
  
  logEvent(analytics, 'feedback_submitted', {
    feedback_type: feedbackType,
    timestamp: new Date().toISOString()
  });
};

// Error tracking
export const trackError = (errorType, errorMessage, context = {}) => {
  if (!analytics) return;
  
  logEvent(analytics, 'error', {
    error_type: errorType,
    error_message: errorMessage,
    ...context,
    timestamp: new Date().toISOString()
  });
};

// Performance tracking
export const trackPageLoad = (pageName, loadTime) => {
  if (!analytics) return;
  
  logEvent(analytics, 'page_load', {
    page_name: pageName,
    load_time_ms: loadTime,
    timestamp: new Date().toISOString()
  });
};

export const trackFileUploadPerformance = (fileType, fileSize, uploadTime) => {
  if (!analytics) return;
  
  logEvent(analytics, 'file_upload_performance', {
    file_type: fileType,
    file_size_kb: fileSize,
    upload_time_ms: uploadTime,
    timestamp: new Date().toISOString()
  });
};

// Export performance for custom traces
export { performance };
